import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useRoutes } from 'react-router';

// MUI
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

// common
import MuiLoading from 'common/components/MuiLoading';
import { AppState } from '@redux/store';
import { getBrand } from 'config/brand';

// screen
import { PROJECT_LIST_ROUTES } from 'layout/routes';
import DefaultAside from 'layout/main/DefaultAside';
import DefaultHeader from 'layout/main/DefaultHeader';
import { hideRestrictedMenus, routesByUserRoles } from 'layout/main/utils';
import useIsParent from 'common/hook/useIsParent';
import useIsGuest from 'common/hook/useIsGuest';
import useEngagementReporter from 'common/hook/useEngagementReporter';

const ProcessLoading = React.memo(
  () => {
    const loading = useSelector((state: AppState) => state.common.loading, shallowEqual);
    if (loading) {
      return (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            left: 0,
            zIndex: 100,
          }}
        />
      );
    }
    return null;
  },
  () => true
);

const DefaultLayout = () => {
  const userRoles = useSelector(({ auth: { userRoles } }: AppState) => userRoles, shallowEqual);
  const isParent = useIsParent();
  const isGuest = useIsGuest();
  // Gắn một lần ở layout gốc: báo thời gian trang hiển thị lên BE (ẩn tab / rời trang).
  useEngagementReporter();
  // Phụ huynh/khách: ẩn menu QR NCC TPAT (route vẫn giữ để gõ URL bị đẩy về Dashboard).
  const mappedRoutes = hideRestrictedMenus(
    { isParent, isGuest },
    routesByUserRoles(userRoles, PROJECT_LIST_ROUTES)
  );
  const element = useRoutes(mappedRoutes as any);

  return (
    <Box display="flex" bgcolor={'background.default'}>
      <DefaultAside listRouter={mappedRoutes} />
      <Box
        className="relative flex-1 h-screen flex flex-col overflow-auto shrink-0 min-w-[100vw] tablet:min-w-0"
        sx={(theme) => ({
          [theme.breakpoints.down('mobile')]: {
            minWidth: '100vw',
          },
        })}
      >
        <DefaultHeader />
        <ProcessLoading />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            gap: { xs: 1.5, laptop: 2.5 },
            p: { xs: 1.5, laptop: 2.5 },
          }}
        >
          <React.Suspense fallback={<MuiLoading />}>{element}</React.Suspense>
        </Box>
        <Box sx={{ px: { xs: 1.5, laptop: 2.5 }, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {getBrand().copyright}
          </Typography>
        </Box>
      </Box>
      <Box id="aside-right" />
    </Box>
  );
};

export default DefaultLayout;
